import { singularize as singularizeUtil } from 'ember-inflector';
import { isBlank, isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import moment from 'moment-precise-range-plugin';
import { SECONDS_IN_DAY } from 'cio/utils/constants';
import { dateIsWithinThreshold, unixNowSeconds } from 'cio/utils/time';
import { MessageDisplay } from 'cio/app-models/in-app/in-app-message';
import { capitalize } from 'cio/helpers/capitalize';
import pluralizeUtil from 'cio/utils/pluralize';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import joinWords from 'cio/utils/join-words';
import TemplateModel from 'cio/models/template';
import { Editor } from 'cio/services/editor';

export const DEFAULT_RECIPIENT = '{{customer.email}}';
export const RECIPIENT_REQUIRED_ERROR = 'Recipient is required';

export enum InAppMessageDeliveryPriority {
  High = 1,
  Medium = 5,
  Low = 10,
}

export const MESSAGE_PRIORITY_LABELS = {
  [InAppMessageDeliveryPriority.High]: 'High',
  [InAppMessageDeliveryPriority.Medium]: 'Medium',
  [InAppMessageDeliveryPriority.Low]: 'Low',
};

export enum ModalPosition {
  Top = 'top',
  Center = 'center',
  Bottom = 'bottom',
}

export enum OverlayPosition {
  TopLeft = 'x-gist-floating-top-left',
  TopCenter = 'x-gist-floating-top',
  TopRight = 'x-gist-floating-top-right',
  BottomLeft = 'x-gist-floating-bottom-left',
  BottomCenter = 'x-gist-floating-bottom',
  BottomRight = 'x-gist-floating-bottom-right',
}

export enum DisplayMode {
  Modal = 'modal',
  Overlay = 'overlay',
  Inline = 'inline',
}

export enum RuleOperator {
  Contains = 'contains',
  Equals = 'equals',
}

const OVERLAY_LABEL: Record<OverlayPosition, string> = {
  [OverlayPosition.TopLeft]: 'Top Left',
  [OverlayPosition.TopCenter]: 'Top Center',
  [OverlayPosition.TopRight]: 'Top Right',
  [OverlayPosition.BottomLeft]: 'Bottom Left',
  [OverlayPosition.BottomCenter]: 'Bottom Center',
  [OverlayPosition.BottomRight]: 'Bottom Right',
};

export type InAppPlatform = 'web' | 'android' | 'ios';

/*
 * Those rules determine the right screen to display an in-app message.
 */
export class InAppPageRule {
  @tracked platform: InAppPlatform = 'web';
  @tracked operator = RuleOperator.Contains;
  @tracked value = '';

  constructor(operator?: RuleOperator, value?: string, platform?: InAppPlatform) {
    this.operator = operator ?? RuleOperator.Contains;
    this.value = value ?? '';
    this.platform = platform ?? 'web';
  }

  get hasErrors() {
    return this.errors.length > 0;
  }

  get errors(): string[] {
    return isBlank(this.value) ? ["can't be blank"] : [];
  }

  serialize() {
    let { operator, value } = this;
    return { operator, value };
  }
}

export const RULE_OPERATORS_OPTIONS = [
  { label: 'is equal to', value: RuleOperator.Equals },
  { label: 'contains', value: RuleOperator.Contains },
];

export enum InAppExpirationType {
  Absolute = 'absolute',
  Relative = 'relative',
}

export const IN_APP_EXPIRATION_THRESHOLD_DAYS = 60;
// IN_APP_EXPIRATION_THRESHOLD_DAYS constant multiplied by: 60 (seconds in one minute) * 60 (minutes in an hour) * 24 (hours)
const IN_APP_EXPIRATION_THRESHOLD_SECONDS = IN_APP_EXPIRATION_THRESHOLD_DAYS * 86400;
const DEFAULT_EXPIRATION_DAYS = 30;
export const DEFAULT_EXPIRATION_IN_SECONDS = DEFAULT_EXPIRATION_DAYS * SECONDS_IN_DAY;

export type Unit = 'minute' | 'hour' | 'day';
export type PluralizedUnit = 'minutes' | 'hours' | 'days';

export const pluralize = (unit: Unit): PluralizedUnit => {
  return pluralizeUtil(2, unit) as PluralizedUnit;
};

export const singularize = (pluralizedUnit: PluralizedUnit): Unit => {
  return singularizeUtil(pluralizedUnit) as Unit;
};

export const hasAbsoluteDateBeenSelectedOnDifferentTz = (options?: InAppMessageConfigUpdate['options']) => {
  return !isNone(options?.isAbsoluteDateSelectedOnDifferentTz) && options!.isAbsoluteDateSelectedOnDifferentTz;
};

export const pageRuleSummary = (rules?: InAppPageRule[]) => {
  let isPlatformConfigured = (rules?: InAppPageRule[], platform?: InAppPlatform) => {
    return rules?.some((rule) => rule.platform === platform) ?? false;
  };

  let configuredRules = [];
  if (isPlatformConfigured(rules, 'web')) {
    configuredRules.push('Web');
  }
  if (isPlatformConfigured(rules, 'ios')) {
    configuredRules.push('iOS');
  }
  if (isPlatformConfigured(rules, 'android')) {
    configuredRules.push('Android');
  }
  let total = rules?.length ?? 0;
  return `${formatNumberAndPluralize([total, 'rule'])} (${joinWords(configuredRules)}${total < 3 ? ' only' : ''})`;
};

export class InAppMessageExpiration {
  @tracked type = InAppExpirationType.Relative;
  @tracked value: number | undefined = DEFAULT_EXPIRATION_IN_SECONDS;
  @tracked liquidExpression = '';
  @tracked liquidUnitRelative: Unit | undefined;

  static absolute(date: moment.Moment) {
    return new InAppMessageExpiration({ type: InAppExpirationType.Absolute, value: date.unix() });
  }

  static relative(seconds: number) {
    return new InAppMessageExpiration({ type: InAppExpirationType.Relative, value: seconds });
  }

  static liquid(expirationType: InAppExpirationType, liquidExpression: string, unit?: Unit) {
    return new InAppMessageExpiration({ type: expirationType, liquidExpression, unit });
  }

  constructor({
    type,
    value,
    liquidExpression,
    unit,
  }: {
    type: InAppExpirationType;
    value?: number;
    liquidExpression?: string;
    unit?: Unit;
  }) {
    this.type = type;
    if (value != null) {
      this.value = value;
      this.liquidExpression = '';
    }
    if (liquidExpression != null) {
      this.liquidExpression = liquidExpression;
      this.value = undefined;
      this.liquidUnitRelative = unit;
    }
  }

  get errors() {
    if (!this.isLiquidExpression) {
      if (this.isRelativeDate) {
        if (this.isValidRelativeDate) {
          return [];
        }
        // If the value is invalid, could be negative or bigger than the threshold
        if (Number(this.value) > IN_APP_EXPIRATION_THRESHOLD_SECONDS) {
          return [`should not be greater than ${IN_APP_EXPIRATION_THRESHOLD_DAYS} days in the future`];
        }
        return ['should be a integer'];
      } else {
        if (!this.isValidAbsoluteDate) {
          return ['should be in the future'];
        }
        if (dateIsWithinThreshold(this.value) === false) {
          return [`should not be greater than ${IN_APP_EXPIRATION_THRESHOLD_DAYS} days in the future`];
        }
        return [];
      }
    }
    return [];
  }

  get isRelativeDate() {
    return this.type === InAppExpirationType.Relative;
  }

  get isLiquidExpression() {
    return this.liquidExpression != null && this.value == null;
  }

  get isValidRelativeDate() {
    if (isNone(this.value)) {
      return false;
    }
    let seconds = Number(this.value);
    return Number.isInteger(seconds) && seconds > 0 && seconds <= IN_APP_EXPIRATION_THRESHOLD_SECONDS;
  }

  get isValidAbsoluteDate() {
    return !isNone(this.value) && this.value > unixNowSeconds();
  }

  get hasErrors() {
    return this.errors.length > 0;
  }

  serialize() {
    let { type, value } = this;
    if (isNone(value)) {
      value = 0;
    }
    if (this.isLiquidExpression) {
      if (this.liquidUnitRelative) {
        // Pluralized units
        return { type, value: this.liquidExpression, unit: pluralize(this.liquidUnitRelative) };
      }
      return { type, value: this.liquidExpression };
    }
    return { type, value };
  }
}

// default values
export const IN_APP_DEFAULT_MESSAGE_PRIORITY = InAppMessageDeliveryPriority.Low;
export const IN_APP_DEFAULT_MESSAGE_DISPLAY_POSITION = ModalPosition.Top;

export interface InAppMessageConfig {
  expiration?: InAppMessageExpiration;
  display?: MessageDisplay;
  position?: ModalPosition;
  priority?: InAppMessageDeliveryPriority;
  routeRules?: InAppPageRule[];
  persistent?: boolean;
}

export interface InAppMessageConfigUpdate extends InAppMessageConfig {
  options?: {
    isAbsoluteDateSelectedOnDifferentTz?: boolean;
  };
}

export const inAppMessageDeliveryPriorityLabel = (priority?: string | number) => {
  let label = '';
  if (!isNone(priority)) {
    let key = Number(priority) as InAppMessageDeliveryPriority;
    label = MESSAGE_PRIORITY_LABELS[key] ?? '';
  }
  return label;
};

export const inAppMessageDisplayLabel = (display: MessageDisplay) => {
  let valueLabel = display.value;
  if (display.mode === DisplayMode.Modal) {
    valueLabel = capitalize([display.value]);
  } else if (display.mode === DisplayMode.Overlay) {
    valueLabel = OVERLAY_LABEL[display.value as OverlayPosition];
  }
  let label = capitalize([display.mode]);
  if (valueLabel && valueLabel.length > 0) {
    label += `, ${valueLabel}`;
  }
  return label;
};

export const isVisualEditorInAppMessage = (template: TemplateModel) => {
  return template.templateType === 'in_app' && template.editor === Editor.VISUAL;
};

export const getTemplateName = (template?: TemplateModel) => {
  if (template?.action != null) {
    return template.action.name;
  } else if (template?.newsletter != null) {
    return template.newsletter.name;
  }
  return;
};
